import { useEffect } from 'react';
import { Snackbar } from '@gapartners/ui';
import { CaseFormDialog } from '@/components/contact-center/CaseFormDialog';
import { useCaseFormEditor } from '@/components/contact-center/useCaseFormEditor';
import type { CaseInformationResponse } from '@/types/case';

export interface CallHistoryCaseDetailsDialogProps {
  selectedCaseId: number | null;
  contactId: string | null;
  workspaceId?: number | null;
  onClose: () => void;
  isCaseLoading: boolean;
  caseError: unknown;
  caseData: CaseInformationResponse | undefined;
}

export function CallHistoryCaseDetailsDialog({
  selectedCaseId,
  contactId,
  workspaceId,
  onClose,
  isCaseLoading,
  caseError,
  caseData,
}: CallHistoryCaseDetailsDialogProps) {
  const isOpen = selectedCaseId !== null;

  const {
    caseFields,
    initialCaseFields,
    handleFormFieldChange,
    hasFieldChanged,
    hasUnsavedChanges,
    handleDiscardNotes,
    handleSaveAll,
    isSaving,
    statusOptions,
    subCaseTypeTree,
    originationOptions,
    priorityOptions,
    caseTypeOptions,
    categories,
    employeeOptions,
    internalUserOptions,
    resolutionTypeOptions,
    clientContactOptions,
    toast,
    clearToast,
  } = useCaseFormEditor({
    caseInfo: caseData,
    contactId,
    syncWhen: isOpen && Boolean(caseData),
    workspaceId,
  });

  useEffect(() => {
    if (isOpen) clearToast();
  }, [selectedCaseId, isOpen, clearToast]);

  const handleClose = () => {
    clearToast();
    onClose();
  };

  return (
    <>
      <CaseFormDialog
        mode="edit"
        open={isOpen}
        onClose={handleClose}
        onSubmit={() => void handleSaveAll()}
        isLoading={isSaving}
        formFields={caseFields}
        onFormFieldChange={handleFormFieldChange}
        statusOptions={statusOptions}
        subCaseTypeTree={subCaseTypeTree}
        originationOptions={originationOptions}
        priorityOptions={priorityOptions}
        caseTypeOptions={caseTypeOptions}
        categories={categories}
        currentCase={caseData ?? null}
        initialCaseFields={initialCaseFields}
        hasFieldChanged={hasFieldChanged}
        hasUnsavedChanges={hasUnsavedChanges}
        onDiscardNotes={handleDiscardNotes}
        contactId={contactId}
        isCaseLoading={isCaseLoading}
        caseLoadError={Boolean(caseError)}
        closeCaseLoopSource="caseRecord"
        employeeOptions={employeeOptions}
        internalUserOptions={internalUserOptions}
        resolutionTypeOptions={resolutionTypeOptions}
        clientContactOptions={clientContactOptions}
        relatedCaseWorkspaceId={
          workspaceId != null ? String(workspaceId) : null
        }
      />

      <Snackbar
        open={!!toast}
        onClose={clearToast}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </>
  );
}
